import { Scene, Clock } from "three";
import { createRenderer } from "./engine/renderer";
import { createCamera, createCameraPath } from "./engine/camera";
import { createSmoothScroll } from "./scroll/lenis";
import { createTimeline } from "./scroll/timeline";
import { ChapterManager } from "./chapters";
import { createCh0Photon } from "./chapters/ch0_photon";
import { createCh1Weak } from "./chapters/ch1_weak";
import { createCh2Gluon } from "./chapters/ch2_gluon";
import { createCh3Higgs } from "./chapters/ch3_higgs";
import { createCh4Release } from "./chapters/ch4_release";
import { createSandboxControls } from "./sandbox/controls";
import { BosonEmitter } from "./sandbox/boson_emitter";
import type { BosonKind } from "./sandbox/particles";
import { mountHud } from "./ui/hud";
import { mountLangToggle } from "./ui/lang_toggle";
import { createComposer } from "./engine/postprocess";
import { detectFromBrowser, settingsForTier } from "./engine/tier";
import { FpsMonitor } from "./perf/monitor";
import { t } from "./i18n/t";

function hasWebGL(): boolean {
  try {
    const c = document.createElement("canvas");
    return !!(c.getContext("webgl2") || c.getContext("webgl"));
  } catch { return false; }
}

const canvas = document.querySelector<HTMLCanvasElement>("#scene")!;

if (!hasWebGL()) {
  document.body.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;height:100vh;flex-direction:column;font-family:Inter,sans-serif;color:#e6edf3;background:#05070b;text-align:center;padding:24px;">
      <img src="/poster.jpg" alt="" style="max-width:80vw;max-height:50vh;border-radius:12px;margin-bottom:24px;" />
      <p>${t("fallback_no_webgl")}</p>
    </div>`;
} else {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    queueMicrotask(() => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "auto" }));
  }

  const settings = settingsForTier(detectFromBrowser());
  const renderer = createRenderer(canvas);
  const camera = createCamera();
  const scene = new Scene();
  let composer = createComposer(renderer, scene, camera, settings);

  // WebGL context-loss / restore
  canvas.addEventListener("webglcontextlost", (e) => {
    e.preventDefault();
    console.warn("[gauge] webgl context lost");
  });
  canvas.addEventListener("webglcontextrestored", () => {
    console.warn("[gauge] webgl context restored — reload");
    location.reload();
  });

  createSmoothScroll();
  const path = createCameraPath();
  let progress = 0;

  const hud = mountHud(document.body);
  mountLangToggle(document.body);

  createTimeline({
    camera, path,
    onProgress: (p) => { progress = p; hud.setProgress(p); },
  });

  const sandbox = createSandboxControls(camera, canvas);
  const emitter = new BosonEmitter(camera, canvas);
  emitter.attach(scene);
  emitter.bindClicks();

  hud.root.querySelectorAll<HTMLButtonElement>('#sandbox-hud button[data-k]').forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      hud.root.querySelectorAll<HTMLButtonElement>('#sandbox-hud button[data-k]')
        .forEach((b) => b.classList.remove("armed"));
      btn.classList.add("armed");
      emitter.arm(btn.dataset.k as BosonKind);
    });
  });
  (hud.root.querySelector("#reset") as HTMLElement).addEventListener("click", (e) => {
    e.stopPropagation();
    emitter.reset();
  });
  (hud.root.querySelector("#replay") as HTMLElement).addEventListener("click", (e) => {
    e.stopPropagation();
    window.scrollTo({ top: 0, behavior: "smooth" });
  });

  const chapters = new ChapterManager(
    [
      createCh0Photon(), createCh1Weak(), createCh2Gluon(), createCh3Higgs(),
      createCh4Release(
        () => { sandbox.enable(); hud.showSandboxHud(true); },
        () => { sandbox.disable(); hud.showSandboxHud(false); },
      ),
    ],
    { scene, camera },
  );

  const fpsGuard = new FpsMonitor({
    windowFrames: 60, threshold: 45, sustainSec: 3,
    onDowngrade: () => {
      composer = null;
      console.warn("[gauge] downgraded postprocess due to low fps");
    },
  });

  const clock = new Clock();
  function tick() {
    const dt = clock.getDelta();
    chapters.setProgress(progress, clock.elapsedTime, dt);
    sandbox.update(clock.elapsedTime, dt);
    emitter.tick(dt);
    fpsGuard.tick(clock.elapsedTime, dt);
    if (composer) composer.render(); else renderer.render(scene, camera);
    requestAnimationFrame(tick);
  }
  tick();
}
